import {
    AttachmentFullViewState,
    AttachmentMenuAction,
    isSaveToCloudApplicable,
} from 'owa-attachment-full-data';
import {
    getOfficeOnlineAppFromExtension,
    OfficeOnlineApp,
    getExtensionFromFileName,
} from 'owa-file';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import AttachmentModel from 'owa-attachment-model-store/lib/store/schema/AttachmentModel';
import isMenuActionSupported from '../../utils/isMenuActionSupported';
import { isDataProviderAvailable } from 'owa-fileprovider-store/lib/utils/isDataProviderAvailable';
import { appPowerPoint } from 'owa-locstrings/lib/strings/apppowerpoint.locstring.json';
import { appExcel } from 'owa-locstrings/lib/strings/appexcel.locstring.json';
import { appWord } from 'owa-locstrings/lib/strings/appword.locstring.json';
import {
    EditInBrowserUploadingText,
    EditInBrowserOpeningText,
    EditInBrowserErrorText,
} from '../../strings.locstring.json';
import loc, { isStringNullOrWhiteSpace } from 'owa-localize';
import { ReferenceAttachmentModel, AttachmentConstants } from 'owa-attachment-model-store';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';
import { confirm } from 'owa-confirm-dialog';
import { getODCDriveItem } from 'owa-fileprovider-link-services/lib/getFileInfo/getODCDriveItem';
import * as trace from 'owa-trace';

const EXCEL_SVG = require('../../resources/Excel_16x.svg');
const POWERPOINT_SVG = require('../../resources/PowerPoint_16x.svg');
const WORD_SVG = require('../../resources/Word_16x.svg');

const LOADING_STATIC_PAGE =
    "<style>body{background-color:#f7f7f7;font-size:24px;color:#605e5c;font-family:'Segoe UI Web (West European)',Segoe UI,-apple-system,BlinkMacSystemFont,Roboto,Helvetica Neue,sans-serif}#flexContainer{display:flex;height:100%;padding:0;margin:0;align-items:center;flex-direction:column;justify-content:center}#imgContainer{width:120px;height:120px;margin-bottom:5px;display:flex;flex-direction:column-reverse;align-items:center;justify-content:baseline}#spinner{box-sizing:border-box;border-radius:50%;border:1.5px solid;border-color:#0078d4 #c7e0f4 #c7e0f4;animation-name:speen;animation-duration:1.3s;animation-iteration-count:infinite;animation-timing-function:cubic-bezier(.53,.21,.29,.67);height:40px;width:40px}@keyframes speen{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}</style><div id=flexContainer><div id=imgContainer><div id=spinner></div></div><div id=textContainer><span id=textContent>Uploading to OneDrive...</span></div></div>";

const LOADING_PAGE_TEXT_ID = 'textContent';

const LOADING_PAGE_SPINNER_ID = 'spinner';

export function shouldShowWovenContextMenuItem(
    attachment: AttachmentFullViewState,
    isConsumer?: boolean
): boolean {
    const attachmentModel = getAttachment(attachment.attachmentId);
    const isGroup = attachment.attachmentId.mailboxInfo?.type === 'GroupMailbox';
    const isConsumerGroup = isConsumer && isGroup;
    const officeOnlineApp = getOfficeAppFromAttachment(attachmentModel);

    const showSaveToCloud = !(
        !attachmentModel ||
        !isMenuActionSupported(AttachmentMenuAction.SaveToCloud, attachment) ||
        !isSaveToCloudApplicable(attachment) ||
        isConsumerGroup ||
        !isDataProviderAvailable(isGroup)
    );

    const showViewInProvider = !(
        !isMenuActionSupported(AttachmentMenuAction.ViewInProvider, attachment) ||
        !attachment.strategy.isViewingInProviderSupported ||
        !attachmentModel
    );

    const showOpenInNewTab = !(
        !isMenuActionSupported(AttachmentMenuAction.OpenInNewTab, attachment) ||
        !attachment.strategy.isOpeningInProviderSupported ||
        !attachmentModel ||
        isStringNullOrWhiteSpace((<ReferenceAttachmentModel>attachmentModel).openUrl)
    );

    if (!(showViewInProvider || showSaveToCloud || showOpenInNewTab) || officeOnlineApp === null) {
        return false;
    }

    return true;
}

export function getOfficeAppIconProps(officeOnlineApp: OfficeOnlineApp) {
    let appIcon = '';

    switch (officeOnlineApp) {
        case OfficeOnlineApp.Word:
            appIcon = WORD_SVG;
            break;
        case OfficeOnlineApp.Excel:
            appIcon = EXCEL_SVG;
            break;
        case OfficeOnlineApp.PowerPoint:
            appIcon = POWERPOINT_SVG;
            break;
        default:
            // no assert never here because officeOnlineApp can be null
            break;
    }
    return {
        src: appIcon,
        width: 16,
        height: 16,
        shouldFadeIn: false,
    };
}

export function getOfficeAppDisplayName(officeOnlineApp: OfficeOnlineApp) {
    let appDisplayName = '';

    switch (officeOnlineApp) {
        case OfficeOnlineApp.Word:
            appDisplayName = appWord;
            break;
        case OfficeOnlineApp.Excel:
            appDisplayName = appExcel;
            break;
        case OfficeOnlineApp.PowerPoint:
            appDisplayName = appPowerPoint;
            break;
        default:
            // no assert never here because officeOnlineApp can be null
            break;
    }
    return appDisplayName;
}

export function getOfficeAppFromAttachment(attachmentModel: AttachmentModel) {
    const extension: string = getExtensionFromFileName(attachmentModel?.model?.Name);
    const officeOnlineApp = getOfficeOnlineAppFromExtension(extension);
    return officeOnlineApp;
}

export async function editIn(
    browser: boolean,
    currentWindow: Window,
    attachmentModel: AttachmentModel,
    officeOnlineApp: OfficeOnlineApp,
    isConsumer: boolean,
    isCloudy: boolean,
    parentComponentName: string,
    isSaveToCloudDisabled: boolean,
    isSaveToCloudSupported: boolean,
    saveToCloud: () => Promise<any>
) {
    currentWindow = currentWindow || window;
    let newWindow: Window = null;
    try {
        if (browser) {
            newWindow = currentWindow.open('about:blank', '_blank');
            if (!newWindow) {
                throw new Error('Cannnot open new tab');
            }

            newWindow.document.write(LOADING_STATIC_PAGE);
            newTabUpdateMessage(newWindow, loc(EditInBrowserUploadingText));
        }

        if (!isCloudy) {
            if (isSaveToCloudDisabled) {
                trace.errorThatWillCauseAlert(`${parentComponentName}-saveToCloudDisabled`);
                return;
            } else if (isSaveToCloudSupported && saveToCloud) {
                await saveToCloud();
            }
        }

        newTabUpdateMessage(newWindow, loc(EditInBrowserOpeningText));

        const attachment = attachmentModel;
        if (attachment) {
            const referenceAttachment = attachment as ReferenceAttachmentModel;
            const referenceAttachmentType = attachment.model as ReferenceAttachment;
            const originalUrl =
                attachment.model.AttachmentOriginalUrl ||
                referenceAttachment.originalUrl ||
                referenceAttachment.openUrl ||
                referenceAttachmentType.AttachLongPathName;

            if (
                !newWindow &&
                ((!isCloudy && isConsumer) /* classic attachment saved to ODC */ ||
                    (isCloudy &&
                        referenceAttachmentType.ProviderType ===
                            AttachmentConstants.providers
                                .ONE_DRIVE_CONSUMER)) /* cloudy attachment on ODC */
            ) {
                openUrl(
                    (await getODCDriveItem(originalUrl)).webDavUrl,
                    newWindow,
                    officeOnlineApp,
                    currentWindow
                );
            } else {
                openUrl(originalUrl, newWindow, officeOnlineApp, currentWindow);
            }
        }
    } catch (error) {
        if (newWindow) {
            newTabUpdateMessage(
                newWindow,
                loc(EditInBrowserErrorText),
                true /* removeLoadingSpinner */
            );
        } else {
            confirm(loc(EditInBrowserErrorText), null, false /* resolveImmediately */, {
                targetWindow: currentWindow,
                hideCancelButton: true,
            });
        }
    }
}

function openUrl(
    originalUrl: string,
    newWindow: Window,
    officeOnlineApp: OfficeOnlineApp,
    currentWindow: Window
) {
    if (newWindow) {
        const querySeparator = originalUrl.indexOf('?') === -1 ? '?' : '&';
        newWindow.location.href = originalUrl + querySeparator + 'web=1';
    } else {
        const appOpenPrefix =
            officeOnlineApp === OfficeOnlineApp.Word
                ? 'ms-word:ofe|u|'
                : officeOnlineApp === OfficeOnlineApp.Excel
                ? 'ms-excel:ofe|u|'
                : officeOnlineApp === OfficeOnlineApp.PowerPoint
                ? 'ms-PowerPoint:ofe|u|'
                : null;
        currentWindow.location.href = appOpenPrefix + originalUrl;
    }
}

function newTabUpdateMessage(newWindow: Window, message: string, removeLoadingSpinner?: boolean) {
    if (!newWindow) {
        return;
    }

    const textContainer = newWindow.document.getElementById(LOADING_PAGE_TEXT_ID);
    if (textContainer) {
        textContainer.innerText = message;
    }

    if (removeLoadingSpinner) {
        const spinner = newWindow.document.getElementById(LOADING_PAGE_SPINNER_ID);
        spinner?.parentNode?.removeChild(spinner);
    }
}
